/**
 * `/c/:symbol/d/:id` — the side-by-side viewer. The screen the app exists
 * for (§10.4).
 *
 * The left pane is the image the model was actually sent; the right is what
 * it returned. They anchor on **page id**, not on a bounding box: no boxes
 * exist (§2.8), so linking scroll offsets between the panes would be
 * theatre. Page N on the left, everything extracted from page N on the
 * right.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftToLine, ArrowRightToLine, ChevronLeft, ChevronRight,
  FileText, Keyboard, MoreHorizontal, Replace, ScrollText,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams, useSearch } from 'wouter'

import type { DocumentResponse, RailPage } from '@server/sql/documents.ts'
import type { PagePayload } from '@server/sql/page.ts'
import { ApiError, api, keys, queryClient } from '~/lib/api.ts'
import { useDocumentTitle } from '~/lib/title.ts'
import { useT } from '~/i18n/index.tsx'
import { bytes, count, date, percent0 } from '~/i18n/format.ts'
import { Badge, Button, IconButton, Label, Skeleton, bandOf } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { SplitPane } from '~/components/ui/split.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { Dialog } from '~/components/ui/overlay.tsx'
import { Raw, Untranslated } from '~/components/ui/raw.tsx'
import { VIEWER_STACKS, VIEWER_STACKS_Y, useMediaQuery } from '~/lib/media.ts'
import { PageRail } from '~/features/viewer/rail.tsx'
import { ImagePane, type Fit, type ImageChoice } from '~/features/viewer/image-pane.tsx'
import { ExtractionPane } from '~/features/viewer/extraction-pane.tsx'
import { DigitProvider, useDigitMode } from '~/features/viewer/digits.tsx'
import { ShortcutsDialog } from '~/features/viewer/shortcuts.tsx'
import { RawReplyDialog } from '~/features/viewer/raw-reply.tsx'
import { RenderStatus } from '~/features/viewer/render-status.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'

export function DocumentScreen() {
  const { symbol = '', id = '' } = useParams<{ symbol: string; id: string }>()
  const documentId = Number(id)
  const { t, tn, locale, v } = useT()
  const superadmin = useSuperadmin()
  const search = useSearch()
  const [location, navigate] = useLocation()
  const qc = useQueryClient()

  const [fit, setFit] = useState<Fit>('width')
  const [digits, toggleDigits] = useDigitMode()
  const [choiceKey, setChoiceKey] = useState<string | null>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showRaw, setShowRaw] = useState(false)
  const [showDetails, setShowDetails] = useState(false)

  /*
   * The layout the viewport can actually carry.
   *
   *   stacked  one pane at a time, chosen by a switch. A phone.
   *   axis y   stacked panes with a draggable divider. A portrait tablet:
   *            a filing page is portrait, so full width and half the height
   *            beats half the width and all of it.
   *   axis x   the desktop reading -- and the one this screen is for.
   */
  const stacked = useMediaQuery(VIEWER_STACKS)
  const splitY = useMediaQuery(VIEWER_STACKS_Y)
  const axis = splitY ? 'y' : 'x'
  // Pages whose file appeared while this screen was open. The rail reads it
  // so a document visibly fills in rather than needing a reload.
  const [arrived, setArrived] = useState<ReadonlySet<number>>(() => new Set())
  const onPageArrived = useCallback((pageNo: number) => {
    setArrived((prev) => (prev.has(pageNo) ? prev : new Set(prev).add(pageNo)))
  }, [])

  const { data: doc, error, refetch } = useQuery({
    queryKey: keys.document(documentId),
    queryFn: () => api<DocumentResponse>(`/api/documents/${documentId}`),
    enabled: Number.isFinite(documentId) && documentId > 0,
  })

  // The page is a URL parameter, so a link to page 14 of document 41 is a
  // link to page 14 of document 41.
  //
  // `p` is the canonical name and the one `setPage` writes. `page` is
  // accepted as an alias because it is what anyone hand-writing a link
  // reaches for first -- and because links carrying it are already out
  // there. A URL that silently opens page 1 instead of the page it names is
  // worse than one that is refused: the reader has no way to tell that the
  // citation did not land.
  const pages = doc?.pages ?? []
  const first = pages[0]?.pageNo ?? 1
  const params = new URLSearchParams(search)
  const requested = Number(params.get('p') ?? params.get('page'))
  const pageNo = pages.some((p) => p.pageNo === requested) ? requested : first

  /*
   * Before the early returns below, so this hook runs on every render --
   * conditioning it on `doc` would change the hook count between the
   * loading and loaded states, which React does not allow.
   *
   * The page number is in the title because it is the thing that changes
   * fastest: this is the one screen where a person pages through dozens of
   * states a minute, and "3010 · Q1 2024 · p12" is what tells two tabs on
   * the same filing apart at a glance.
   */
  useDocumentTitle(
    doc
      ? [
          doc.document.symbol,
          [v('periodShort', doc.document.period), doc.document.fiscalYear].filter(Boolean).join(' '),
          `p${pageNo}`,
        ].filter(Boolean).join(' · ')
      : symbol || undefined,
  )

  const setPage = useCallback(
    (n: number) => {
      const sp = new URLSearchParams(search)
      sp.delete('page')   // the alias never survives a navigation
      sp.set('p', String(n))
      navigate(`${location}?${sp}`, { replace: true })
    },
    [search, location, navigate],
  )

  /*
   * Which pane is showing, when only one can. In the URL beside `p`, for the
   * same reason `p` is: every screen's state is in the URL (§10), so a link
   * sent from a phone opens on the pane it was sent from and survives a
   * reload. It is simply ignored on a screen wide enough for both.
   */
  const paneParam = new URLSearchParams(search).get('pane')
  const pane: 'image' | 'extraction' = paneParam === 'extraction' ? 'extraction' : 'image'
  const setPane = useCallback(
    (next: 'image' | 'extraction') => {
      const sp = new URLSearchParams(search)
      if (next === 'image') sp.delete('pane')
      else sp.set('pane', next)
      navigate(`${location}${sp.toString() ? `?${sp}` : ''}`, { replace: true })
    },
    [search, location, navigate],
  )

  const rail = pages.find((p) => p.pageNo === pageNo)

  const { data: payload } = useQuery({
    queryKey: keys.documentPage(documentId, pageNo),
    queryFn: () => api<PagePayload>(`/api/documents/${documentId}/pages/${pageNo}`),
    enabled: !!rail,
  })

  // --- prefetch ±2 ------------------------------------------------------
  // Page turns then feel instant, and 40 pages of Tier B is ~30MB, which the
  // browser cache holds comfortably (§10.4).
  useEffect(() => {
    if (!doc) return
    for (const delta of [1, -1, 2, -2]) {
      const target = pages.find((p) => p.pageNo === pageNo + delta)
      if (!target) continue
      void qc.prefetchQuery({
        queryKey: keys.documentPage(documentId, target.pageNo),
        queryFn: () => api<PagePayload>(`/api/documents/${documentId}/pages/${target.pageNo}`),
      })
      if (target.hasImage) {
        const img = new Image()
        img.src = `/media/page/${target.id}`
      }
    }
  }, [doc, pages, pageNo, documentId, qc])

  // --- images available for this page ----------------------------------
  const choices: ImageChoice[] = useMemo(
    () =>
      (payload?.images ?? []).map((i) => ({
        url: i.url, tier: i.tier, source: i.role === 'as-sent' ? 'extraction' : i.role,
        index: i.index, overlap: i.overlap,
      })),
    [payload],
  )

  /**
   * The whole page, unless a crop was asked for.
   *
   * `payload.images` arrives crops-first, because §2.5 orders it by
   * *evidence value*: a Tier C crop is the image the model was actually
   * sent, and that is the right first answer to "what did it read". It is
   * the wrong first thing to *show*. Opening on one half of a remuneration
   * table, already zoomed, with no indication that the other half exists,
   * reads as a broken render rather than as a deliberate crop.
   *
   * So the default is the full page and the crops stay one click away, where
   * the `as sent` caption explains what they are. A tier the reader picks is
   * still remembered while paging (below).
   */
  const fullPage =
    choices.find((c) => c.source !== 'extraction') ??
    choices.find((c) => c.tier === 'A' || c.tier === 'A0')

  const choice =
    choices.find((c) => `${c.tier}:${c.index}` === choiceKey) ??
    fullPage ?? choices[0] ?? null

  // A tier chosen on one page is kept on the next where it exists -- paging
  // through a document at Tier B should not silently drop to Tier A.
  useEffect(() => {
    if (!choiceKey || choices.length === 0) return
    if (!choices.some((c) => `${c.tier}:${c.index}` === choiceKey)) {
      const sameTier = choices.find((c) => c.tier === choiceKey.split(':')[0])
      setChoiceKey(sameTier ? `${sameTier.tier}:${sameTier.index}` : null)
    }
  }, [choices, choiceKey])

  // --- navigation helpers ------------------------------------------------
  const step = useCallback(
    (delta: number) => {
      const i = pages.findIndex((p) => p.pageNo === pageNo)
      const next = pages[Math.min(pages.length - 1, Math.max(0, i + delta))]
      if (next) setPage(next.pageNo)
    },
    [pages, pageNo, setPage],
  )

  /**
   * The one that matters: on a 40-page filing with two failing checks,
   * walking every page to find them is the difference between using this app
   * and not (§10.4).
   */
  const stepFailure = useCallback(
    (dir: 1 | -1) => {
      const i = pages.findIndex((p) => p.pageNo === pageNo)
      const order = dir === 1
        ? [...pages.slice(i + 1), ...pages.slice(0, i)]
        : [...pages.slice(0, i).reverse(), ...pages.slice(i + 1).reverse()]
      const hit = order.find((p) => p.checks.failed > 0)
      if (hit) setPage(hit.pageNo)
    },
    [pages, pageNo, setPage],
  )

  const jumpToStatement = useCallback(
    (n: number) => {
      const statements = pages.filter((p) => p.extractor === 'statement')
      const target = statements[n - 1]
      if (target) setPage(target.pageNo)
    },
    [pages, setPage],
  )

  // --- keyboard ---------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      // Never steal a key from a text field or an open dialog's controls.
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)))
        return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      // In RTL chrome the arrow keys still mean previous/next *page*, not
      // previous/next in visual order: the rail does not mirror, and neither
      // does the document.
      switch (e.key) {
        case 'ArrowRight': case 'j':
          e.preventDefault(); e.shiftKey ? stepFailure(1) : step(1); break
        case 'ArrowLeft': case 'k':
          e.preventDefault(); e.shiftKey ? stepFailure(-1) : step(-1); break
        case 'Home': e.preventDefault(); setPage(first); break
        case 'End': e.preventDefault(); setPage(pages.at(-1)?.pageNo ?? first); break
        case 'f': setFit('width'); break
        case '0': setFit('page'); break
        case '1': case '2': case '3': case '4':
          // 1 is both "actual size" and "first statement page". The plan
          // gives both; statements win, because that is the paging task, and
          // 1:1 has its own button.
          jumpToStatement(Number(e.key)); break
        case 'd': toggleDigits(); break
        case 'r': setShowRaw(true); break
        case 'p':
          if (doc?.document.sourcePdf.available)
            window.open(`/media/pdf/${documentId}#page=${pageNo}`, '_blank', 'noopener')
          break
        case '?': setShowShortcuts(true); break
        case 'Escape': setShowShortcuts(false); setShowRaw(false); break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [step, stepFailure, jumpToStatement, setPage, first, pages, doc, documentId, pageNo, toggleDigits])

  // --- render ------------------------------------------------------------
  if (error) {
    const e = error as ApiError
    return (
      <div className="mx-auto w-full max-w-[46rem] px-3 py-6 sm:px-4">
        {e.status === 404 ? (
          <EmptyState title={e.message}>
            <Link href={`/c/${symbol}`} className="link-inline">
              {t('nav.back')}
            </Link>
          </EmptyState>
        ) : (
          <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
            onRetry={() => void refetch()} retryLabel={t('action.retry')} />
        )}
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="flex min-h-0 flex-1">
        <Skeleton className="h-full w-[86px] rounded-none" />
        <Skeleton className="h-full flex-1 rounded-none" />
      </div>
    )
  }

  const d = doc.document
  // The failing-page jump is a quality affordance: without the verdicts it
  // would be a shortcut to pages the reader has no way to tell apart.
  const anyFailure = superadmin && pages.some((p) => p.checks.failed > 0)
  const failingPages = pages.filter((p) => p.checks.failed > 0).length
  const band = bandOf(d.quality.checks > 0 ? d.quality.passed / d.quality.checks : null)
  const home = symbol && symbol !== '-' ? `/c/${symbol}` : `/c/${d.symbol}`
  const passRate = d.quality.checks ? d.quality.passed / d.quality.checks : null

  const passRateEl = (
    <span
      className={cn(
        'tnum text-[length:var(--text-data)]',
        band === 'pass' ? 'text-[var(--pass)]'
          : band === 'warn' ? 'text-[var(--warn)]' : 'text-[var(--fail)]',
      )}
      title={t('quality.passRateOf', {
        pct: percent0(d.quality.passed / (d.quality.checks || 1), locale),
        passed: count(d.quality.passed, locale),
        total: count(d.quality.checks, locale),
      })}
    >
      {percent0(passRate, locale)}
    </span>
  )

  /*
   * The filing's identity. Fourteen items in one wrapping row is five or six
   * rows on a phone, on the one screen that has a page image to show -- so
   * below `lg` everything but the four that change how a figure is *read*
   * moves into the details sheet. Nothing is dropped; it is one tap away and
   * it is the same content in the same order.
   */
  const secondary = (
    <>
      {d.periodEnd && (
        <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
          → {date(d.periodEnd, locale)}
        </span>
      )}
      {d.assurance && (
        <Badge state="neutral">{v('assurance', d.assurance) ?? d.assurance}</Badge>
      )}
      {/* Basis and rounding decide what every figure on the page means:
          136 of these filings are consolidated and 95 standalone, and 54
          print in thousands. Reading one of those as units is a
          thousandfold error, so neither is left implicit. */}
      {d.basis && <Badge state="neutral">{v('basis', d.basis) ?? d.basis}</Badge>}
      {d.rounding && (
        <Badge state={d.rounding === 'units' ? 'neutral' : 'warn'}>
          {d.currency ? `${d.currency} ` : ''}
          {v('rounding', d.rounding) ?? d.rounding}
        </Badge>
      )}
      {d.run?.xbrlAgreement !== null && d.run?.xbrlAgreement !== undefined && (
        <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
          XBRL {percent0(d.run.xbrlAgreement, locale)}
        </span>
      )}
      {d.entityNameAr && (
        <Raw lang="ar" className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {d.entityNameAr}
        </Raw>
      )}
    </>
  )

  const actions = (
    <>
      {d.sourcePdf.available && (
        <a
          href={`/media/pdf/${documentId}#page=${pageNo}`}
          target="_blank"
          rel="noopener"
          title={
            d.sourcePdf.bytes
              ? `${t('viewer.pdf')} · ${bytes(d.sourcePdf.bytes, locale)} · p`
              : 'p'
          }
        >
          <Button size="sm"><FileText size={15} />{t('viewer.pdf')}</Button>
        </a>
      )}
      <Button size="sm" onClick={() => setShowRaw(true)} title="r">
        <ScrollText size={15} />{t('viewer.raw')}
      </Button>
      <Button size="sm" onClick={toggleDigits} title="d">
        <Replace size={15} />
        {digits === 'printed' ? t('viewer.digitsPrinted') : t('viewer.digitsWestern')}
      </Button>
    </>
  )

  const imagePane = (
    <ImagePane
      pageId={rail?.id ?? 0}
      pageNo={pageNo}
      choices={choices}
      choice={choice}
      onChoice={(c) => setChoiceKey(`${c.tier}:${c.index}`)}
      fit={fit}
      onFit={setFit}
      onStep={step}
      renderCommand={`python -m parser.render --document-id ${documentId} --tier B`}
      missingReason={rail?.hasImage ? null : 'not_planned'}
    />
  )

  const extractionPane = payload ? (
    <ExtractionPane
      payload={payload}
      rail={rail}
      symbol={symbol && symbol !== '-' ? symbol : d.symbol}
      documentId={documentId}
    />
  ) : (
    <div className="space-y-2 p-3">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  )

  return (
    <DigitProvider mode={digits}>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* --- document header ---------------------------------------- */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[var(--border)] bg-[var(--bg-raised)] px-2 py-1.5 sm:px-3">
          <Link
            href={home}
            className={cn(
              'relative tap flex min-h-[var(--control-h-sm)] shrink-0 items-center gap-1 rounded-[var(--radius-control)]',
              'pe-1 text-[length:var(--text-data)] text-[var(--text-muted)]',
              'hover:text-[var(--text)] active:text-[var(--text)]',
            )}
          >
            <ChevronLeft size={16} className="rtl:-scale-x-100" />
            <span className="tnum font-[family-name:var(--font-mono)]">{d.symbol}</span>
          </Link>
          <Sep />
          <span className="text-[length:var(--text-data)]">
            {v('docType', d.docType) ?? <Untranslated>{d.docType}</Untranslated>}
          </span>
          <span className="tnum text-[length:var(--text-data)] text-[var(--text-muted)]">
            {d.period && (v('period', d.period) ?? d.period)} {d.fiscalYear}
          </span>

          <span className="hidden items-center gap-2 lg:flex">{secondary}</span>

          <Badge state={d.status === 'verified' ? 'pass' : 'warn'}>
            {v('docStatus', d.status) ?? d.status}
          </Badge>
          {superadmin && passRateEl}

          <div className="flex-1" />

          <span className="hidden items-center gap-2 lg:flex">{actions}</span>

          {/* Everything the row could not hold, at every width where it
              could not hold it. */}
          <IconButton
            className="lg:hidden"
            label={t('viewer.details')}
            size="sm"
            active={showDetails}
            onClick={() => setShowDetails(true)}
          >
            <MoreHorizontal size={16} />
          </IconButton>
          {/* Hidden by a wrapper: see the note in components/header.tsx. */}
          <span className="hidden lg:inline-flex">
            <IconButton
              label={t('viewer.shortcuts')}
              size="sm"
              onClick={() => setShowShortcuts(true)}
            >
              <Keyboard size={16} />
            </IconButton>
          </span>
        </div>

        <RenderStatus
          documentId={documentId}
          tier={choice?.tier ?? 'B'}
          onPageArrived={onPageArrived}
        />

        {/* --- the pane switch, when only one pane fits ---------------- */}
        {stacked && (
          <div className="border-b border-[var(--border)] bg-[var(--bg-raised)] px-2 py-1.5">
            <Segmented
              fill
              ariaLabel={t('viewer.paneLabel')}
              value={pane}
              onChange={setPane}
              options={[
                { id: 'image' as const, label: t('viewer.paneImage') },
                {
                  id: 'extraction' as const,
                  label: (
                    <span className="inline-flex items-baseline gap-1.5">
                      {t('viewer.paneExtraction')}
                      {rail && (
                        <span className="tnum text-[length:var(--text-micro)] opacity-70">
                          {count(rail.extracted.facts + rail.extracted.tables, locale)}
                        </span>
                      )}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        )}

        {/* --- panes --------------------------------------------------
            Stacked: a filmstrip above one pane. Otherwise the rail beside a
            split whose axis the viewport chooses. */}
        {stacked ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <PageRail
              orientation="horizontal"
              pages={pages}
              current={pageNo}
              onSelect={setPage}
              arrived={arrived}
            />
            <div className="min-h-0 flex-1">
              {pane === 'image' ? imagePane : extractionPane}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            <PageRail pages={pages} current={pageNo} onSelect={setPage} arrived={arrived} />
            <SplitPane
              axis={axis}
              className="min-h-0 flex-1"
              defaultRatio={axis === 'y' ? 0.58 : 0.52}
              left={imagePane}
              right={extractionPane}
            />
          </div>
        )}

        {/* --- footer -------------------------------------------------
            The primary control on a touchscreen, so it is sized for one and
            padded out of the home indicator. */}
        <div className="pb-safe border-t border-[var(--border)] bg-[var(--bg-raised)]">
          <div className="flex items-center gap-1 px-2 py-1 sm:gap-2 sm:px-3">
            <IconButton label={t('viewer.prevFailure')} disabled={!anyFailure}
              onClick={() => stepFailure(-1)}>
              <ArrowLeftToLine size={16} />
            </IconButton>
            <IconButton label={t('nav.back')} onClick={() => step(-1)}>
              <ChevronLeft size={18} />
            </IconButton>

            {/* The counter is a control, not a caption: on a 368-page filing
                the way to page 200 was the rail or nothing. */}
            <Button
              size="sm"
              className="tnum min-w-[7rem] justify-center sm:min-w-[9rem]"
              onClick={() => setShowDetails(true)}
              title={t('viewer.goToPage')}
            >
              {t('viewer.pageOf', { n: pageNo, total: pages.length })}
            </Button>

            <IconButton label={t('viewer.page', { n: pageNo + 1 })} onClick={() => step(1)}>
              <ChevronRight size={18} />
            </IconButton>
            <IconButton label={t('viewer.nextFailure')} disabled={!anyFailure}
              onClick={() => stepFailure(1)}>
              <ArrowRightToLine size={16} />
            </IconButton>

            <div className="flex-1" />

            {/* Whether there is anywhere to jump to, before you press the
                button that jumps there. */}
            {/* Both halves are verdicts -- "no failures" says as much about
                the checks as "2 failing pages" does -- so the pair is gated
                together rather than falling back to the reassuring one. */}
            {superadmin && (anyFailure ? (
              <span className="tnum shrink-0 text-[length:var(--text-micro)] text-[var(--fail)]">
                {tn('viewer.failingPages', failingPages, { n: count(failingPages, locale) })}
              </span>
            ) : (
              <span className="shrink-0 text-[length:var(--text-micro)] text-[var(--pass)]">
                {t('viewer.noFailures')}
              </span>
            ))}
            {rail && (
              <span className="tnum hidden shrink-0 text-[length:var(--text-micro)] text-[var(--text-faint)] sm:inline">
                {count(rail.extracted.facts, locale)} facts · {count(rail.extracted.tables, locale)} tables
                {rail.checks.failed > 0 && (
                  <span className="ms-1.5 text-[var(--fail)]">
                    {t('viewer.checksFailed', { n: rail.checks.failed })}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* --- the details sheet ---------------------------------------- */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        title={t('viewer.details')}
        closeLabel={t('action.close')}
        width={460}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">{secondary}</div>

          <div className="flex flex-col gap-2">
            <Label>{t('viewer.goToPage')}</Label>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => { setPage(first); setShowDetails(false) }}>
                <ArrowLeftToLine size={15} className="rtl:-scale-x-100" />
                {t('viewer.firstPage')}
              </Button>
              <Button
                size="sm"
                onClick={() => { setPage(pages.at(-1)?.pageNo ?? first); setShowDetails(false) }}
              >
                <ArrowRightToLine size={15} className="rtl:-scale-x-100" />
                {t('viewer.lastPage')}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">{actions}</div>

          <div>
            <Button size="sm" onClick={() => { setShowDetails(false); setShowShortcuts(true) }}>
              <Keyboard size={15} />
              {t('viewer.shortcuts')}
            </Button>
          </div>
        </div>
      </Dialog>

      <ShortcutsDialog open={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <RawReplyDialog
        open={showRaw}
        onClose={() => setShowRaw(false)}
        documentId={documentId}
        pageNo={pageNo}
      />
    </DigitProvider>
  )
}

const Sep = () => <span className="text-[var(--border-strong)]">·</span>
